import { formatters } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import {
  Button,
  HiddenVisually,
  Text,
  Stack,
  Box,
  IconLocation,
  IconMail,
  Column,
  Columns,
  useResponsiveValue,
} from 'braid-design-system';
import { useEffect } from 'react';

import { CardError } from 'src/components/Card/CardError/CardError';
import { CardTitle } from 'src/components/Card/CardHeading/CardTitle/CardTitle';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { CREATE_RESUME_PAGE, PERSONAL_DETAILS } from 'src/components/Router';
import { Link } from 'src/components/Router/Link';
import { usePage } from 'src/components/Router/usePage';
import { EDIT } from 'src/consts';
import { PERSONAL_DETAILS_FORM } from 'src/context/FormContext';
import { analyticsClient } from 'src/modules/analytics';
import { useAppConfig } from 'src/modules/appConfig';
import { timeToInteractive } from 'src/modules/performance-metrics';
import { useRetryAction } from 'src/modules/retry-actions/hooks';

import { setRouteAsTouched } from '../../CreateResume/actionCreators';
import { EditPersonalDetails } from '../EditPersonalDetails/EditPersonalDetails';
import { usePersonalDetails } from '../data';

import translations from './.vocab';
import { InlineNudge } from './InlineNudge/InlineNudge';
import { ShareableProfileShareButton } from './ShareShareableProfile/ShareableProfileShareButton';

export const ReadPersonalDetails = () => {
  useEffect(() => {
    timeToInteractive();
  }, []);
  const { t } = useTranslations(translations);
  const { brand, isUnifiedSite } = useAppConfig();
  const { personalDetails, loading, error } = usePersonalDetails();
  const firstName = personalDetails?.personalDetails.firstName || '';
  const lastName = personalDetails?.personalDetails.lastName || '';
  const name = formatters.formatName(firstName, lastName);
  const emailAddress = personalDetails?.emailAddress;
  const location = personalDetails?.currentLocation2?.shortDescription || '';
  const title = t('Edit personal details');
  const retryAction = useRetryAction(PERSONAL_DETAILS);
  const showErrorAlert = Boolean(retryAction?.canRetry);
  const page = usePage();

  const showLoader = Boolean(loading || error);

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
  });

  return (
    <>
      <Box
        id={PERSONAL_DETAILS}
        data-automation="personal-details"
        position="relative"
      >
        <HiddenVisually>
          <h1>{name ? name : t('Brand profile', { brand })}</h1>
          <CardTitle name={PERSONAL_DETAILS} title={t('Personal details')} />
        </HiddenVisually>

        <Stack space="large">
          {showErrorAlert ? <CardError card={PERSONAL_DETAILS} /> : null}
          {showLoader ? (
            <LoadingItem
              variant="transparent"
              type="heading"
              level="1"
              width="large"
            />
          ) : (
            <InlineNudge
              type="Heading"
              nudgeText={t('Add your name')}
              fieldToFocus="first-name"
              displayText={name}
              data={{ automation: 'inline-nudge-name' }}
            />
          )}
          <Stack space="small">
            {showLoader ? (
              <LoadingItem
                variant="transparent"
                type="text"
                size="standard"
                width="small"
              />
            ) : (
              <InlineNudge
                type="Text"
                nudgeText={t('Add a home location')}
                fieldToFocus="current-location"
                displayText={location}
                icon={<IconLocation tone="secondary" />}
                data={{ automation: 'inline-nudge-location' }}
              />
            )}

            {showLoader ? (
              <LoadingItem
                variant="transparent"
                type="text"
                size="standard"
                width="medium"
              />
            ) : (
              <Text
                maxLines={1}
                icon={<IconMail tone="secondary" />}
                data={{
                  'hj-masked': 'true',
                  automation: 'personal-detail-email',
                }}
              >
                {emailAddress}
              </Text>
            )}
          </Stack>
          {showLoader ? (
            <LoadingItem variant="transparent" type="button" />
          ) : (
            <Columns space="none">
              <Column width={(!isMobile && '3/5') || undefined}>
                <Columns space="xsmall">
                  <Column width={(!isMobile && '1/5') || undefined}>
                    <Link
                      card={PERSONAL_DETAILS}
                      mode={EDIT}
                      component={Button}
                      variant="ghost"
                      tone="neutral"
                      data={{ automation: 'personal-details-edit' }}
                      aria-label={t('Edit personal details')}
                      onClick={() => {
                        analyticsClient.cardOpened({
                          currentPageSection: 'personal details',
                          currentPageSectionState: 'edit',
                        });

                        if (page === CREATE_RESUME_PAGE) {
                          setRouteAsTouched(PERSONAL_DETAILS);
                        }
                      }}
                    >
                      {t('Edit')}
                    </Link>
                  </Column>
                  {isUnifiedSite ? (
                    <Column width={(!isMobile && '1/5') || undefined}>
                      <ShareableProfileShareButton />
                    </Column>
                  ) : null}
                </Columns>
              </Column>
            </Columns>
          )}
        </Stack>
      </Box>
      <FormDrawer
        title={title}
        component={EditPersonalDetails}
        card={PERSONAL_DETAILS}
        formName={PERSONAL_DETAILS_FORM}
      />
    </>
  );
};
